import React, { useEffect, useState } from 'react';
import { StickyNote, Edit2, Trash2, Save, X, Send } from 'lucide-react';
import { Button } from './Button';
import { Textarea } from './Textarea';
import { notesService, Note, NoteEntityType } from '@/services/notes.service';
import { useAuth } from '@/hooks/useAuth';
import toast from 'react-hot-toast';

interface Props {
  entityType: NoteEntityType;
  entityUid: string;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export const NotesTab: React.FC<Props> = ({ entityType, entityUid }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [composer, setComposer] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');

  const refresh = () =>
    notesService.list(entityType, entityUid)
      .then(setNotes)
      .catch(() => setNotes([]));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [entityType, entityUid]);

  const create = async () => {
    if (!composer.trim()) return;
    setBusy(true);
    try {
      await notesService.create(entityType, entityUid, composer.trim());
      setComposer('');
      await refresh();
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const startEdit = (n: Note) => { setEditingUid(n.noteUid); setEditingBody(n.body); };
  const cancelEdit = () => { setEditingUid(null); setEditingBody(''); };

  const saveEdit = async (n: Note) => {
    if (!editingBody.trim()) return;
    setBusy(true);
    try {
      await notesService.update(n.noteUid, editingBody.trim());
      cancelEdit();
      await refresh();
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const remove = async (n: Note) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm('Delete this note?')) return;
    setBusy(true);
    try {
      await notesService.remove(n.noteUid);
      await refresh();
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const canEdit = (n: Note) => n.authorUserUid === user?.userUid;
  const canDelete = (n: Note) => n.authorUserUid === user?.userUid || user?.role === 'company_admin';

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-surface-alt/40 p-3">
        <Textarea
          rows={3}
          placeholder="Add a note… use @userUid to mention a teammate."
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); create(); }
          }}
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-2xs text-text-muted">⌘/Ctrl+Enter to post</span>
          <Button variant="primary" size="sm" onClick={create} loading={busy} disabled={!composer.trim() || busy}>
            <Send size={12} /> Post note
          </Button>
        </div>
      </div>

      {notes === null && (
        <div className="space-y-2">
          {[0, 1].map((i) => <div key={i} className="skeleton h-16 rounded-lg" />)}
        </div>
      )}

      {notes && notes.length === 0 && (
        <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-text-muted">
          <StickyNote size={20} className="mx-auto mb-1 opacity-50" />
          No notes yet
        </div>
      )}

      {notes && notes.length > 0 && (
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.noteUid} className="rounded-lg border border-border bg-surface p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-text-primary">{n.authorUserUid}</span>
                <div className="flex items-center gap-1">
                  <span className="text-2xs text-text-muted" title={new Date(n.createdAt).toLocaleString()}>
                    {formatTimestamp(n.createdAt)}{n.editedAt ? ' (edited)' : ''}
                  </span>
                  {editingUid !== n.noteUid && canEdit(n) && (
                    <button onClick={() => startEdit(n)} className="rounded p-1 text-text-muted hover:bg-surface-alt hover:text-text-primary" title="Edit">
                      <Edit2 size={12} />
                    </button>
                  )}
                  {canDelete(n) && (
                    <button onClick={() => remove(n)} className="rounded p-1 text-text-muted hover:bg-danger-soft hover:text-danger" title="Delete">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>

              {editingUid === n.noteUid ? (
                <div className="mt-2">
                  <Textarea rows={3} value={editingBody} onChange={(e) => setEditingBody(e.target.value)} />
                  <div className="mt-2 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={busy}><X size={12} /> Cancel</Button>
                    <Button variant="primary" size="sm" onClick={() => saveEdit(n)} loading={busy} disabled={busy}><Save size={12} /> Save</Button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 whitespace-pre-line text-sm text-text-primary leading-relaxed">{n.body}</p>
              )}

              {n.mentions.length > 0 && editingUid !== n.noteUid && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {n.mentions.map((m) => (
                    <span key={m} className="rounded-full bg-info-soft px-1.5 py-0.5 text-2xs text-info">@{m}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
